import type { ReactNode, SelectHTMLAttributes } from "react";
import Field from "../Field/Field";
import "./Select.css";
import "../Control/Control.css";

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> {
  id: string;
  options: SelectOption[];
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode | boolean;
  /** first, empty choice that cannot be picked again, for example "Please select" */
  placeholder?: string;
  size?: "sm" | "md";
  className?: string;
}

/**
 * Native select with the label above and the same look as TextField.
 * Example: <Select id="lang" label="Language" options={[{ value: "es", label: "Español" }]} />
 */
const Select = ({
  id,
  options,
  label,
  hint,
  error,
  placeholder,
  size = "md",
  className = "",
  ...selectProps
}: SelectProps) => (
  <Field
    htmlFor={id}
    label={label}
    hint={hint}
    error={error as ReactNode}
    className={className}
  >
    <div
      className={`ds-control ds-control--${size} ds-select ${
        selectProps.disabled ? "is-disabled" : ""
      } ${
        selectProps.value === "" || selectProps.value == null
          ? "is-placeholder"
          : ""
      }`}
    >
      <select
        id={id}
        className="ds-control__input"
        aria-invalid={error ? true : undefined}
        {...selectProps}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className="ds-select__chevron"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  </Field>
);

export default Select;
